import { cn } from "../lib/utils";
import { ChevronRight } from "lucide-react";

export type Quiz = {
  id: string;
  vidtoquiz_tests: {
    id: string;
    title: string;
    show_answers: boolean;
    show_quizwizard_branding: boolean;
  };
  question_text: string;
  image_url?: string;
  vidtoquiz_answer_options: {
    id: string;
    option_text: string;
    is_correct: boolean;
    image_url?: string;
  }[];
  answer_explanation?: string;
};

export type quizAnswers = Record<string, string>;

export const QnaSection = ({
  mcq,
  quizAnswers,
  handleClickOption,
  handleClickNext,
  currentIndex,
  totalQuestions,
  showAnswer,
}: {
  mcq: Quiz;
  quizAnswers: quizAnswers;
  totalQuestions: number;
  currentIndex: number;
  handleClickOption?: (optionId: string, showAnswer?: boolean) => void;
  handleClickNext?: (mcq: Quiz) => void;
  showAnswer?: boolean;
}) => {
  return (
    <div
      className={cn(
        "wor-mx-auto wor-flex wor-h-full wor-w-full wor-max-w-screen-md wor-flex-col wor-items-center wor-space-y-4 wor-rounded-xl wor-border wor-border-black/10 wor-bg-white wor-bg-gradient-to-r wor-from-gray-400 wor-to-gray-300 wor-p-2 wor-shadow-[0_3px_10px_rgb(0,0,0,0.2)]",
        showAnswer && "wor-bg-gradient-to-r wor-from-gray-200 wor-to-gray-300",
      )}
    >
      <div className="wor-min-w-[90%] wor-rounded-lg wor-bg-white wor-p-6">
        <div className="wor-mb-4 wor-h-1.5 wor-w-full wor-rounded-full wor-bg-gray-200">
          <div
            className="wor-h-1.5 wor-rounded-full wor-bg-orange-500"
            style={{
              width: `${((currentIndex + 1) / totalQuestions) * 100}%`,
            }}
          ></div>
        </div>
        <div className="wor-flex wor-w-full wor-items-center wor-justify-between">
          <div className="wor-flex wor-w-16 wor-cursor-pointer wor-items-center wor-justify-center wor-rounded-md wor-border-none wor-bg-gradient-to-r wor-from-orange-500 wor-to-amber-500 wor-p-1 wor-text-lg wor-text-white">
            {currentIndex + 1}/{totalQuestions}
          </div>
        </div>
        <div className="wor-rounded-sm wor-bg-white wor-text-xl wor-font-semibold">
          <div class="wor-mb-2 wor-mt-4">{mcq.question_text}</div>
          {mcq?.image_url && (
            <img
              src={mcq.image_url}
              alt="Question"
              className="wor-mb-4 wor-aspect-[16/9] wor-w-64 wor-rounded-md wor-object-cover"
            />
          )}
        </div>
        <div className="">
          <div className="wor-grid wor-w-full wor-grid-cols-1 wor-flex-col wor-gap-6 wor-pt-4 sm:wor-grid-cols-2">
            {mcq.vidtoquiz_answer_options &&
              mcq.vidtoquiz_answer_options.map((option) => (
                <div
                  role="button"
                  key={option.id}
                  className={cn(
                    "wor-flex wor-cursor-pointer wor-items-center wor-space-x-4 wor-rounded-lg wor-border wor-border-gray-400 wor-p-2",
                    quizAnswers[currentIndex] === option.id
                      ? "wor-border-orange-300 wor-bg-orange-100"
                      : " wor-bg-white hover:wor-bg-gray-50",
                  )}
                  onClick={() =>
                    handleClickOption && handleClickOption(option.id)
                  }
                >
                  <div className="wor-flex wor-flex-none wor-items-start wor-justify-center wor-rounded wor-font-semibold">
                    <div
                      type="radio"
                      id={`option-${option.id}`}
                      name={`quiz-option-${currentIndex}`}
                      value={option.id}
                      checked={
                        (quizAnswers &&
                          quizAnswers[currentIndex] === option.id) ||
                        (option.is_correct &&
                          showAnswer &&
                          mcq.vidtoquiz_tests.show_answers)
                      }
                      className={cn(
                        "wor-h-5 wor-w-5 wor-fill-none wor-ring-2 wor-ring-orange-400 checked:wor-bg-orange-400 checked:wor-ring-orange-400 hover:checked:wor-bg-orange-400 active:wor-bg-orange-400 active:wor-ring-orange-400",
                        quizAnswers &&
                          quizAnswers[currentIndex] === option.id &&
                          "wor-bg-orange-400",
                        mcq.vidtoquiz_tests.show_answers &&
                          option.is_correct &&
                          showAnswer &&
                          "wor-ring-green-600 checked:wor-bg-green-600 checked:wor-ring-green-600  hover:checked:wor-bg-green-600 active:wor-bg-green-600 active:wor-ring-green-600",
                      )}
                      disabled={showAnswer}
                    />
                  </div>
                  <div
                    className={cn(
                      "wor-flex wor-w-full wor-flex-col",
                      option?.image_url &&
                        "wor-items-center wor-justify-center wor-text-center",
                    )}
                  >
                    {option?.image_url && (
                      <img
                        src={option.image_url}
                        alt="Option"
                        className="wor-mb-2 wor-h-44 wor-w-44 wor-rounded-md wor-object-cover"
                      />
                    )}
                    <div
                      className={cn(
                        "wor-max-w-xs wor-break-words wor-text-sm wor-font-normal wor-text-slate-700",
                        showAnswer &&
                          mcq.vidtoquiz_tests.show_answers &&
                          option.is_correct &&
                          "wor-text-green-700",
                      )}
                    >
                      {option.option_text}
                      {showAnswer &&
                        mcq.vidtoquiz_tests.show_answers &&
                        option.is_correct && (
                          <span className="wor-font-semibold wor-text-green-700">
                            : Correct Answer
                          </span>
                        )}
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </div>
      <div className="wor-flex wor-w-full wor-flex-row wor-items-center wor-justify-between">
        {mcq.vidtoquiz_tests.show_quizwizard_branding && !showAnswer && (
          <div className="wor-my-1 wor-flex wor-items-center wor-space-x-1 wor-text-xs wor-text-gray-500 sm:wor-text-sm">
            <div className="wor-font-medium wor-text-white">Powered by</div>
            <div
              onClick={() =>
                window.open("https://www.quizwizard.ai/", "_blank")
              }
              className="wor-font-semibold wor-text-white wor-no-underline wor-transition-all wor-duration-150 hover:wor-underline"
              style="cursor: pointer;"
            >
              QuizWizard
            </div>
          </div>
        )}
        {showAnswer && (
          <div className="wor-flex wor-flex-col wor-gap-y-2 wor-p-4">
            <div className="wor-text-base wor-font-semibold">
              Answer Explanation:
            </div>
            <div className="wor-text-sm wor-font-light wor-text-gray-900">
              {mcq.answer_explanation}
            </div>
          </div>
        )}
        {!showAnswer && (
          <div
            role="button"
            className={cn(
              "wor-ml-auto wor-flex wor-w-24 wor-scale-100 wor-transform wor-cursor-pointer  wor-items-center wor-justify-center wor-rounded-md wor-border-none wor-bg-gradient-to-r wor-from-orange-500 wor-to-amber-500 wor-p-2 wor-text-lg wor-font-medium wor-text-white wor-ring-offset-black wor-transition-all wor-duration-300 hover:wor-scale-105 active:wor-scale-95 disabled:wor-pointer-events-none disabled:wor-opacity-50",
              !quizAnswers.hasOwnProperty(currentIndex) &&
                "wor-cursor-not-allowed ",
            )}
            onClick={() => handleClickNext && handleClickNext(mcq)}
          >
            Next <ChevronRight />
          </div>
        )}
      </div>
    </div>
  );
};
